import React, { useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { InputGroup, InputLabel, Select, InputWithUnit, TextInput } from '@components/Common';
import DateTimePicker from '@components/Common/DateTimePicker/DateTimePicker';
import TankBalanceProgress from '@components/Common/TankBalance';
import { Warning } from '@components/Common/SharedStyles';
import { toDateTimePickerString, formatLocalDateTime, startOfDayLocal, toIsoUtc } from '@helpers/dateUtils';
import { parsePositiveInteger } from '@helpers/parsing';
import { Tank, FlowMeterType } from '@api/queries/tanks';
import { Product } from '@api/queries/products';
import { ActionDirection, ShipmentType } from '@api/queries/inventory';
import { EditorMode } from '../hooks/useBookkeepingEntry';
import { BookkeepingFormData } from '../BookkeepingEntryForm';

// Import existing components
import ShipmentTypeDetails, { ShipmentTypeFields } from '../components/ShipmentTypeDetails';

const NarrowInputGroup = styled(InputGroup)`
  max-width: 370px;
`;

interface Props {
  mode: EditorMode;
  tanks: Tank[];
  products: Product[];
  isLocked: boolean;
  isProductMixture: boolean;
}

const ShipmentSection: React.FC<Props> = ({
  mode,
  tanks,
  products,
  isLocked,
  isProductMixture,
}) => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext<BookkeepingFormData>();

  const tankId = watch('tankId');
  const productId = watch('productId');
  const actionDirection = watch('actionDirection');
  const shipmentType = watch('shipmentType');
  const unloadingStarted = watch('unloadingStarted');
  const unloadingStartedTimeSelected = watch('unloadingStartedTimeSelected');
  const unloadingFinished = watch('unloadingFinished');
  const unloadingFinishedTimeSelected = watch('unloadingFinishedTimeSelected');
  const concentrationPercent = watch('concentrationPercent');
  const amount = watch('amount');

  // Carrier data
  const truck = watch('truck');
  const ship = watch('ship');
  const train = watch('train');
  const pipe = watch('pipe');
  const containerTrain = watch('containerTrain');
  const containerTruck = watch('containerTruck');

  const isEdit = mode === 'edit' || mode === 'approved';

  // Action direction options
  const actionDirectionOptions = useMemo(() => {
    const incoming = { value: ActionDirection.Incoming, label: t('entry.directions.incoming') };
    const outgoing = { value: ActionDirection.Outgoing, label: t('entry.directions.outgoing') };
    return isProductMixture ? [outgoing] : [incoming, outgoing];
  }, [t, isProductMixture]);

  // Get selected tank and product
  const selectedTank = useMemo(() => tanks.find((t) => t.id === tankId), [tanks, tankId]);
  const selectedProduct = useMemo(() => products.find((p) => p.id === productId), [products, productId]);

  // Show concentration field
  const hasExistingConcentrationValue = typeof concentrationPercent === 'number';
  const showConcentration =
    (selectedProduct && selectedProduct.concentration != null) || (isEdit && hasExistingConcentrationValue);

  // Tank balance info
  const tankBalance = selectedTank && !isProductMixture ? selectedTank.balance : null;
  const tankSurfaceLevel = selectedTank && !isProductMixture ? selectedTank.surfaceLevel : null;

  // Handle datetime changes
  const handleDateTimeChange = (
    value: string | null,
    hasTime: boolean,
    datePropName: 'unloadingStarted' | 'unloadingFinished',
    isTimeSelectedPropName: 'unloadingStartedTimeSelected' | 'unloadingFinishedTimeSelected'
  ) => {
    let dateTimeString: string | null = null;

    if (value) {
      if (hasTime) {
        dateTimeString = value;
      } else {
        dateTimeString = toIsoUtc(startOfDayLocal(value));
      }
    }

    setValue(datePropName, dateTimeString);
    setValue(isTimeSelectedPropName, hasTime);
  };

  // Handle amount change
  const handleAmountChange = (value: string) => {
    const parsed = parsePositiveInteger(value.replace(/\s/g, ''));
    setValue('amount', parsed);
  };

  // Handle shipment type details change
  const handleShipmentDetailsChange = (newValues: Partial<ShipmentTypeFields>) => {
    Object.entries(newValues).forEach(([key, value]) => {
      setValue(key as keyof BookkeepingFormData, value);
    });
  };

  return (
    <>
      {/* Action Direction */}
      <div className="column">
        <InputGroup>
          <InputLabel htmlFor="actionDirection">{t('entry.action_type')}</InputLabel>
          <Controller
            name="actionDirection"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                name="actionDirection"
                isClearable={false}
                placeholder={t('entry.select_action')}
                value={actionDirectionOptions.find((o) => o.value === field.value) || null}
                onChange={(option) => setValue('actionDirection', option?.value ?? ActionDirection.Outgoing)}
                isDisabled={isLocked}
                options={actionDirectionOptions}
              />
            )}
          />
        </InputGroup>

        {/* Start DateTime */}
        <NarrowInputGroup>
          <DateTimePicker
            title={t('entry.started')}
            name="unloadingStarted"
            showTime={true}
            dateTime={toDateTimePickerString(unloadingStarted, unloadingStartedTimeSelected)}
            hasTime={unloadingStartedTimeSelected}
            changed={(value, hasTime) =>
              handleDateTimeChange(value, hasTime, 'unloadingStarted', 'unloadingStartedTimeSelected')
            }
            isDisabled={isLocked}
          />
        </NarrowInputGroup>

        {/* Finish DateTime */}
        <NarrowInputGroup>
          <DateTimePicker
            title={t('entry.finished')}
            name="unloadingFinished"
            showTime={true}
            dateTime={toDateTimePickerString(unloadingFinished, unloadingFinishedTimeSelected)}
            hasTime={unloadingFinishedTimeSelected}
            changed={(value, hasTime) =>
              handleDateTimeChange(value, hasTime, 'unloadingFinished', 'unloadingFinishedTimeSelected')
            }
            isDisabled={isLocked}
          />
        </NarrowInputGroup>

        {/* Concentration Percent */}
        {showConcentration && (
          <>
            <NarrowInputGroup>
              <InputLabel htmlFor="concentrationPercent">{t('entry.concentration_percent')}</InputLabel>
              <Controller
                name="concentrationPercent"
                control={control}
                render={({ field }) => (
                  <InputWithUnit>
                    <TextInput
                      {...field}
                      name="concentrationPercent"
                      type="number"
                      step="any"
                      value={field.value ?? ''}
                      onChange={(e) => setValue('concentrationPercent', e.target.value ? Number(e.target.value) : null)}
                      disabled={isLocked}
                    />
                    <span>%</span>
                  </InputWithUnit>
                )}
              />
            </NarrowInputGroup>
            {(!selectedTank || selectedTank.flowMeterType !== FlowMeterType.HydrogenPeroxide) && !isLocked && (
              <Warning>{t('entry.warning_tank_has_no_flowmeter')}</Warning>
            )}
          </>
        )}

        {/* Amount */}
        <NarrowInputGroup>
          <InputLabel htmlFor="amount">{t('entry.amount')}</InputLabel>
          <InputWithUnit>
            <TextInput
              name="amount"
              type="text"
              value={(amount && amount.toLocaleString('fi')) || ''}
              onChange={(e) => handleAmountChange(e.target.value)}
              disabled={isLocked}
            />
            <span>kg</span>
          </InputWithUnit>
        </NarrowInputGroup>

        {/* Tank Balance */}
        {tankBalance && (
          <NarrowInputGroup>
            <InputLabel>
              {t('entry.tank_balance')} {formatLocalDateTime(tankBalance.timestamp, true)}:
            </InputLabel>
            <TankBalanceProgress tankBalance={tankBalance} showUnit={true} showKilograms={true} />
          </NarrowInputGroup>
        )}

        {/* Tank Surface Level */}
        {tankSurfaceLevel && (
          <NarrowInputGroup>
            <InputLabel>
              {t('entry.tank_measured_balance')} {formatLocalDateTime(tankSurfaceLevel.timestamp, true)}:
            </InputLabel>
            <TankBalanceProgress tankBalance={tankSurfaceLevel} showUnit={true} showKilograms={true} />
          </NarrowInputGroup>
        )}
      </div>

      {/* Shipment Type */}
      <div className="column">
        <InputGroup>
          <InputLabel htmlFor="shipmentType">{t('entry.shipment_type')}</InputLabel>
          <Controller
            name="shipmentType"
            control={control}
            render={({ field }) => {
              const options = [
                { value: ShipmentType.Truck, label: t('entry.shipment_types.truck') },
                { value: ShipmentType.Ship, label: t('entry.shipment_types.ship') },
                { value: ShipmentType.Pipe, label: t('entry.shipment_types.pipe') },
                { value: ShipmentType.Train, label: t('entry.shipment_types.train') },
                { value: ShipmentType.ContainerTrain, label: t('entry.shipment_types.containertrain') },
                { value: ShipmentType.ContainerTruck, label: t('entry.shipment_types.containertruck') },
              ];
              return (
                <Select
                  {...field}
                  name="shipmentType"
                  isClearable={false}
                  placeholder={t('entry.select_shipment_type')}
                  value={options.find((o) => o.value === field.value) || null}
                  onChange={(option) => setValue('shipmentType', option?.value ?? ShipmentType.Truck)}
                  isDisabled={isLocked}
                  options={options}
                />
              );
            }}
          />
        </InputGroup>

        {/* Shipment Type Details (Truck, Ship, etc.) */}
        <ShipmentTypeDetails
          shipmentType={shipmentType}
          actionDirection={actionDirection}
          truck={truck}
          train={train}
          ship={ship}
          pipe={pipe}
          containerTrain={containerTrain}
          containerTruck={containerTruck}
          onChange={handleShipmentDetailsChange}
          isDisabled={isLocked}
        />
      </div>
    </>
  );
};

export default ShipmentSection;
